<tilby-dialog-toolbar
    [title]="'SETTINGS.HUB.LOGO_EDITOR.TITLE' | translate"
    [hideConfirm]="!canConfirm()"
    (confirm)="confirm()" />

<tilby-dialog-content>
    <div class="tw-flex tw-flex-col tw-gap-4 tw-py-2">
        @if (errorMessage(); as message) {
            <div class="tw-text-center tw-text-red-600" data-error>{{ message }}</div>
        }

        @if (loading()) {
            <div class="tw-text-center tw-text-[var(--sys-secondary-text)]" data-loading>{{ 'SETTINGS.HUB.LOGO_EDITOR.LOADING' | translate }}</div>
        } @else {
            @if (currentLogo(); as logo) {
                <div class="tw-flex tw-flex-col tw-items-end tw-gap-4" data-current>
                    <img [src]="logo" alt="" />
                    <app-action-btn variant="basic" color="warn" (click)="deleteLogo()">
                        <mat-icon>delete</mat-icon>
                        {{ 'SETTINGS.HUB.LOGO_EDITOR.DELETE_LOGO' | translate }}
                    </app-action-btn>
                </div>
            }

            @if (showDropZone()) {
                <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-p-8 tw-cursor-pointer tw-rounded-xl tw-border tw-border-dashed tw-border-[var(--sys-divider)]"
                    data-drop-zone
                    (click)="fileInput.click()"
                    (dragover)="$event.preventDefault()"
                    (drop)="onDrop($event)">
                    <mat-icon class="tw-text-[var(--sys-secondary-text)]">upload_file</mat-icon>
                    <p class="tw-m-0">{{ 'SETTINGS.HUB.LOGO_EDITOR.DROP' | translate }}</p>
                    <span class="tw-text-xs tw-text-[var(--sys-secondary-text)]">{{ 'SETTINGS.HUB.LOGO_EDITOR.DROP_OR_CLICK' | translate }}</span>
                </div>
                <input #fileInput type="file" accept="image/*" class="tw-hidden" (change)="onFileInput($event)" />
            }

            @if (selectedFile(); as file) {
                <!--
                    The cropper paints its surroundings with the color of its overlay, white by default: around the image it looks like a margin
                    of the image itself, that the frame cannot cover. It is made transparent, and the cropper takes the width of the image only.
                -->
                <image-cropper
                    class="tw-w-fit tw-mx-auto"
                    style="--cropper-overlay-color: transparent"
                    [imageFile]="file"
                    [maintainAspectRatio]="false"
                    format="png"
                    output="base64"
                    (imageCropped)="onImageCropped($event)"
                    (loadImageFailed)="onLoadImageFailed()" />
            }

            @if (editedLogo(); as preview) {
                <div class="tw-flex tw-flex-col tw-items-center tw-gap-2" data-preview>
                    <img [src]="preview" alt="" />
                    @if (!currentLogo()) {
                        <div class="tw-text-center tw-text-sm tw-text-red-600">{{ 'SETTINGS.HUB.LOGO_EDITOR.NOT_SAVED_HINT' | translate }}</div>
                    }
                </div>
            }

            @if (selectedFile() || editedLogo()) {
                <div class="tw-flex tw-flex-wrap tw-gap-4">
                    @if (selectedFile()) {
                        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-flex-1 tw-min-w-[10rem]">
                            <mat-label>{{ 'SETTINGS.HUB.LOGO_EDITOR.WIDTH' | translate }}</mat-label>
                            <mat-select name="width" [(ngModel)]="width" panelClass="settings-select-panel">
                                @for (value of widths; track value) {
                                    <mat-option [value]="value">{{ value }}</mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    }
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-flex-1 tw-min-w-[10rem]">
                        <mat-label>{{ 'SETTINGS.HUB.LOGO_EDITOR.BRIGHTNESS' | translate }}</mat-label>
                        <mat-select name="brightness" [(ngModel)]="brightness" panelClass="settings-select-panel">
                            @for (value of brightnesses; track value) {
                                <mat-option [value]="value">{{ value }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>
            }

            @if (editedLogo()) {
                <div class="tw-flex tw-flex-wrap tw-gap-4" data-test-print>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-flex-1 tw-min-w-[10rem]">
                        <mat-label>{{ 'SETTINGS.HUB.LOGO_EDITOR.TEST_IP' | translate }}</mat-label>
                        <input matInput name="testIp" [(ngModel)]="testIp" autocomplete="off" />
                        @if (testIp && !isTestIpValid()) {
                            <mat-hint class="tw-text-red-600">{{ 'NG_MESSAGES.INVALID_VALUE' | translate }}</mat-hint>
                        }
                    </mat-form-field>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="tw-flex-1 tw-min-w-[10rem]">
                        <mat-label>{{ 'SETTINGS.HUB.LOGO_EDITOR.TEST_TYPE' | translate }}</mat-label>
                        <mat-select name="testType" [(ngModel)]="testType" panelClass="settings-select-panel">
                            @for (type of testTypes; track type.value) {
                                <mat-option [value]="type.value">{{ type.label }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                </div>
            }

            @if (selectedFile() || editedLogo()) {
                <div class="tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
                    <app-action-btn variant="basic" color="warn" (click)="resetEdits()">
                        <mat-icon>delete</mat-icon>
                        {{ 'SETTINGS.HUB.LOGO_EDITOR.DELETE_IMAGE' | translate }}
                    </app-action-btn>

                    @if (finalLogo()) {
                        <app-action-btn variant="stroked" [disabled]="!isTestIpValid()" (click)="testLogo()">
                            <mat-icon>print</mat-icon>
                            {{ 'SETTINGS.HUB.LOGO_EDITOR.TEST' | translate }}
                        </app-action-btn>
                    }

                    @if (selectedFile()) {
                        <app-action-btn [disabled]="!croppedImage()" (click)="createLogo()">
                            <mat-icon>check_circle</mat-icon>
                            {{ 'SETTINGS.HUB.LOGO_EDITOR.CREATE' | translate }}
                        </app-action-btn>
                    } @else if (editedLogo()) {
                        <app-action-btn (click)="regenerateLogo()">
                            <mat-icon>autorenew</mat-icon>
                            {{ 'SETTINGS.HUB.LOGO_EDITOR.REGENERATE' | translate }}
                        </app-action-btn>
                    }
                </div>
            }
        }
    </div>
</tilby-dialog-content>
